import { useStore } from '@/utils/useStore'
import ToastsStore from '@/store/toasts'
import EventsStore from '@/store/events'
import GroupsStore from '@/store/groups'
import UsersStore from '@/store/users'
import { Result, Post, Err, UserSpiceLevels, PostVisibility } from '@/classes'
import { postVoteAPI, postGetOneAPI, postEditAPI, postPinAPI } from '@/api/post'

export default defineStore('posts', () => {
  const { getItem, getItems, setItem, deleteItem, updateItem } = useStore('post', { expiration: 30 })

  const toastsStore = ToastsStore()
  const usersStore = UsersStore()
  const eventStore = EventsStore()
  const groupsStore = GroupsStore()

  const create = async <P>(params: P, _id?: string, options?: {
    silent?: boolean
  }) => {
    const response: Result<{ post: Post }> = await fetchAPI('post/create', {
      params, _id: (_id || undefined)
    }, { method: 'POST' })

    if (!response.ok || !response.data) {
      toastsStore.error(response)
      return { ok: false }
    }

    if (!options?.silent) {
      toastsStore.add({ content: useNuxtApp().$i18n.t('components.Post.create.success'), type: 'success' })
    }

    setItem(response.data.post, { priority: 5 })
    
    const subPosts = (response.data.post?.['@posts'] ?? []).forEach((post: Post) => setItem(post, { priority: 5 }))

    return {
      ok: true,
      data: new Post(response.data.post)
    }
  }

  const edit = async <P>(_id: string, params: {
    title?: string
    content?: string
    isSpicy?: boolean
    visibility?: typeof PostVisibility[number]
  }) => {
    const response = await postEditAPI(_id, params)

    if (!response.ok || !response.data) {
      toastsStore.error(response)
      return { ok: false }
    }

    toastsStore.add({ content: useNuxtApp().$i18n.t('components.Post.edit.success'), type: 'success' })

    setItem(response.data.post, { priority: 5 })
    
    return {
      ok: true,
      data: new Post(response.data.post)
    }
  }

  const votePost = async (postId: string, optionId: string) => {
    const response: Result<{ post: Post }> = await postVoteAPI(postId, optionId)

    if (!response.ok || !response.data) {
      toastsStore.error(response)
      return { ok: false }
    }

    const updatedItem = response.data.post
    setItem(updatedItem, { priority: 5 })

    return {
      ok: true,
      data: new Post(updatedItem)
    }
  }

  const pin = async (postId: string, action: boolean) => {
    const response: Result<{ post: Post }> = await postPinAPI(postId, action)

    if (!response.ok || !response.data) {
      toastsStore.error(response)
      return { ok: false }
    }

    const updatedItem = response.data.post
    setItem(updatedItem, { priority: 5 })

    toastsStore.add({
      content: updatedItem.isPinned ? useNuxtApp().$i18n.t('components.Post.pinSuccess') : useNuxtApp().$i18n.t('components.Post.unpinSuccess'),
      type: 'success'
    })

    return {
      ok: true,
      data: new Post(updatedItem)
    }
  }

  const fetchOne = async (postId?: string | null) => {
    if (!postId) return { ok: true, data: null }

    const response = await postGetOneAPI(postId)

    if (!response.ok || !response.data) {
      toastsStore.error(response)
      return { ok: false }
    }

    if (response.data.post) {
      setItem(response.data.post, { priority: 5 })

      if (response.data.post['@posts']) response.data.post['@posts'].forEach(p => setItem(p))
    }

    if (response.data.event) eventStore.setItem(response.data.event)
    if (response.data.group) groupsStore.setItem(response.data.group)
    if (response.data.users) response.data.users.forEach(user => usersStore.setItem(user))

    return {
      ok: true,
      data: response.data.post
    }
  }

  return {
    getItem, getItems, setItem, deleteItem, create, votePost, fetchOne, pin, edit, updateItem
  }
})